/**
 * Represents resources from the /event-types/ API collection and its
 * subcollections.
 */

import type { ActivatedRoute } from '@angular/router';
import { loadIntFilterFromGetParams, loadStringFilterFromGetParams, propExists, propExistsAndIsNonEmpty } from '../reporting-core/utils';
import {
  CurrencyRefundAmount,
  EventDiscountType,
  EventQuestionScope,
  EventQuestionType,
  EventScheduleRepetition,
  EventTypeStatus,
  EventTypeWaitlistScope,
  ScheduledEventsColumnType,
  ScheduleType
} from './event-type-enums';
import { CustomCustomerFieldType, ProfileField } from './organization-config-enums';

export interface ScheduledEventsColumn {
  column_type: ScheduledEventsColumnType;
  profile_field_name?: ProfileField;
  custom_field_name?: string;
  question_name?: string;
}

export interface ConfiguredEventDocumentLocation {
  id: number;
  name: string;
}

export interface ConfiguredEventDocument {
  id: number;
  name: string;
  // Whether the underlying template has been retired (PRO-863). A configured
  // agreement keeps being enforced while retired, but should be replaced.
  is_removed?: boolean;
  locations: ConfiguredEventDocumentLocation[];
}

export interface EventType {
  id: number;
  url: string;
  schedule_type: ScheduleType;
  name: string;
  description_text: string;
  status: EventTypeStatus;
  reserves_area_capacity: boolean;
  late_booking_window_minutes: number;
  is_listed: boolean;
  has_automatic_check_in: boolean;
  max_reservations_per_day_for_customer?: number;
  allow_customer_cancellation: boolean;
  customer_cancellation_window_hours: number;
  customer_cancellation_currency_refund_amount: CurrencyRefundAmount;
  customer_cancellation_currency_refund_as_credit: boolean;
  refund_voucher_on_customer_cancellation: boolean;
  min_participant_age?: number;
  max_participant_age?: number;
  calendar_color_hex: string;
  require_login_for_booking: boolean;
  tax_rate_id: number | null;
  tax_rate_name: string | null;
  max_participant_count: number;
  max_bookings_per_event?: number;
  waitlist_scope: EventTypeWaitlistScope;
  allow_customers_to_join_waitlist: boolean;
  display_waitlist_count_in_climber_app: boolean;
  display_pricing_in_climber_app_listings: boolean;
  display_remaining_capacity_in_climber_app_listings: boolean | null;
  automatically_invite_off_waitlist: boolean;
  min_hours_before_event_for_automatic_waitlist_invite: number;
  hours_to_accept_waitlist_spot: number | null;
  allow_waitlist_customers_to_select_preferred_days_of_week: boolean;
  allow_waitlist_customers_to_select_preferred_locations: boolean;
  require_payment_method_to_join_waitlist: boolean;
  require_membership_ts_and_cs_to_join_waitlist: boolean;
  mailchimp_check_in_participant_event_name: string;
  mailchimp_check_in_booking_customer_event_name: string;
  mailchimp_completed_participant_event_name: string;
  mailchimp_completed_booking_customer_event_name: string;
  scheduled_events_columns: ScheduledEventsColumn[];
  waitlist_staff_notification_email_addresses: string;
  waitlist_confirmation_email_text: string;
  is_private_event_request_form: boolean;
  participant_agreement_documents: ConfiguredEventDocument[];
  required_waiver_documents: ConfiguredEventDocument[];
  pricing_membership_id: number | null;
  has_limited_location_availability: boolean;
  allowed_location_ids: number[];
  allowed_location_names: string[];
  created_at: string;
  updated_at: string;
  questions_url: string;
  schedules_url: string;
  capitan_scheduler_url: string;
}

export interface EventTypeLocationSettings {
  id: number;
  url: string;
  location_id: number;
  location_name: string;
  confirmation_text: string;
  payment_information_text: string;
  send_post_event_email: boolean;
  post_event_email_text: string;
  post_event_email_delay_hours: number;
  post_event_email_attendees_only: boolean;
  send_reminder_email: boolean;
  reminder_email_text: string;
  reminder_email_lead_hours: number;
  send_waiver_email: boolean;
  waiver_email_text: string;
  waiver_email_lead_hours: number;
  waiver_email_document_template_id: number | null;
  waiver_email_document_template_name: string | null;
  staff_notification_email_addresses: string;
  created_at: string;
  updated_at: string;
}

export interface EventQuestion {
  id: number;
  url: string;
  scope: EventQuestionScope,
  question_type: EventQuestionType;
  name: string;
  description: string;
  is_required: boolean;
  all_options_required: boolean;
  options: string;
  custom_customer_field_id: number | null;
  custom_customer_field_name: string | null;
  custom_customer_field_type: CustomCustomerFieldType | null;
  custom_customer_field_options: string[] | null;
  order_number: number;
  created_at: string;
  updated_at: string;
}

export interface EventPerParticipantFee {
  fee: string;
  name: string;
  revenue_account_codes: { [key: string]: string | null };
  includes_entry: boolean;
  sell_voucher_via_pos: boolean;
}

export interface EventPerBookingFee {
  fee: string;
  name: string;
  revenue_account_codes: { [key: string]: string | null };
  includes_entry: boolean;
  sell_voucher_via_pos: boolean;
  included_participants?: number;
  additional_participant_fee?: string;
}

export type EventAddOnMultipleCopiesInputType = 'input' | 'dropdown';

export interface EventPerParticipantAddOn {
  fee: string;
  name: string;
  revenue_account_codes: { [key: string]: string | null };
  allow_multiple_copies?: boolean;
  max_copies?: number;
  multiple_copies_input_type?: EventAddOnMultipleCopiesInputType;
}

export interface EventPerBookingAddOn {
  fee: string;
  name: string;
  revenue_account_codes: { [key: string]: string | null };
  included_participants?: number;
  additional_participant_fee?: string;
  allow_multiple_copies?: boolean;
  max_copies?: number;
  multiple_copies_input_type?: EventAddOnMultipleCopiesInputType;
}

export interface EventPricingMatrix {
  id: number;
  url: string;
  name: string;
  per_participant_fees: EventPerParticipantFee[];
  per_booking_fees: EventPerBookingFee[];
  per_participant_add_ons: EventPerParticipantAddOn[];
  per_booking_add_ons: EventPerBookingAddOn[];
  per_booking_deposit?: string;
  vouchers_include_entry: boolean;
  allow_check_in_before_final_payment: boolean;
  is_removed: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventScheduleInstructor {
  id: number;
  first_name: string;
  last_name: string;
}

export interface EventSchedule {
  id: number;
  url: string;
  roster_id: number | null;
  roster_name: string | null;
  location_id: number;
  location_name: string;
  location_url: string;
  location_area_id?: number;
  location_area_name?: string;
  location_area_url?: string;
  name: string;
  customer_facing_name: string;
  duration_minutes: number;
  capacity: number;
  start_date: string;
  rolling_days: number;
  booking_open_time: string;
  end_date?: string;
  pricing_matrix_id?: number;
  benefit_membership_id: number | null;
  benefit_membership_name: string | null;
  benefit_membership_additional_days: number;
  pricing_matrix_url?: string;
  pricing_matrix_name?: string;
  tax_rate_id: number | null;
  tax_rate_name: string | null;
  repetition: EventScheduleRepetition;
  instructor_details: EventScheduleInstructor[];
  created_at: string;
  updated_at: string;
  time_slots_url: string;
  events_url: string;

  // Write-only fields
  instructor_ids?: number[];
}

// An event which would be left orphaned if a schedule were deleted. Returned by
// the schedule deletion-preview endpoint. On most schedule types that means a
// future event with at least one active, non-cancelled reservation; on a
// rolling one, where everything else is deleted outright, it means an event
// which granted a membership benefit.
export interface ScheduleDeletionPreviewEvent {
  event_id: number;
  event_part_id: number;
  event_type_name: string;
  occurrence_name: string;
  location_name: string;
  // Start datetime in the location's local time, paired with the location's
  // IANA time zone so it can be formatted with the shared date/time helpers.
  start_datetime: string;
  time_zone: string;
  num_active_reservations: number;
}

export interface ScheduleDeletionPreview {
  remaining_events: ScheduleDeletionPreviewEvent[];
  // How much the deletion will remove outright. On a rolling event type the
  // future events are deleted along with their bookings, so remaining_events is
  // usually empty and these counts carry the warning instead.
  removed_event_count: number;
  removed_reservation_count: number;
}

// Why an event survives a ROLLING deletion, which removes everything else
// outright. Passed to the orphaned-events modal in place of its default
// active-reservations wording, which is only true for other schedule types.
export const ROLLING_ORPHAN_REASON_SINGULAR =
  'it granted a membership benefit and cannot be removed automatically';
export const ROLLING_ORPHAN_REASON_PLURAL =
  'they granted membership benefits and cannot be removed automatically';

/**
 * A statement of what deleting a schedule or schedule exception will destroy,
 * or null when it will not remove any bookings.
 *
 * Deleting a rolling schedule or exception removes its upcoming events and
 * every reservation on them, so staff are told the scale before they confirm.
 * Other schedule types never delete a booked event — they leave it behind and
 * warn about it via remaining_events — so the counts are not worth showing.
 *
 * The count is of reservations (participants), not bookings: one booking may
 * hold several reservations, and removed_reservation_count counts the latter.
 *
 * This is a statement rather than a question because it is rendered in two
 * places — a confirm() dialog, whose caller appends the question, and the
 * orphaned-events modal, where the Cancel / Delete Anyway buttons already ask
 * it.
 *
 * @param subjectNoun What is being deleted, e.g. 'schedule exception'.
 * @param preview The deletion preview returned by the API.
 */
export function getScheduleDeletionBookingWarning(
  subjectNoun: string,
  preview: ScheduleDeletionPreview
): string | null {
  if (!preview.removed_reservation_count) {
    return null;
  }

  const eventCount = preview.removed_event_count;
  const reservationCount = preview.removed_reservation_count;

  return `Removing this ${subjectNoun} will also remove `
    + `${eventCount} upcoming ${eventCount === 1 ? 'event' : 'events'} `
    + `and the ${reservationCount} `
    + `${reservationCount === 1 ? 'reservation' : 'reservations'} `
    + `on ${eventCount === 1 ? 'it' : 'them'}. `
    + 'This action cannot be undone.';
}

// What the events a schedule exception creates are called when staff are
// warned about losing them. Reads as "Removing this ...".
const EXCEPTION_REPLACEMENT_NOUN = 'exception’s replacement schedule';

/**
 * A statement of what saving a schedule exception as a plain cancellation will
 * destroy, or null when it has no upcoming events of its own to lose
 * (PRO-1508).
 *
 * Switching an exception from replacing its days' events to simply cancelling
 * them removes the same events deleting the exception would, so it is driven
 * by the same deletion preview.
 *
 * Like getScheduleDeletionBookingWarning this is a statement, not a question;
 * the caller appends one.
 *
 * @param preview The exception's deletion preview, as returned by the API.
 */
export function getExceptionCancellationWarning(
  preview: ScheduleDeletionPreview
): string | null {
  const bookingWarning = getScheduleDeletionBookingWarning(
    EXCEPTION_REPLACEMENT_NOUN,
    preview
  );
  if (bookingWarning) {
    return bookingWarning;
  }

  // Nothing booked is at stake, but events still disappear from the calendar,
  // and that is the whole point of the switch - so it is still worth confirming.
  const eventCount = preview.removed_event_count;
  if (!eventCount) {
    return null;
  }

  return `Removing this ${EXCEPTION_REPLACEMENT_NOUN} will remove `
    + `${eventCount} upcoming ${eventCount === 1 ? 'event' : 'events'}. `
    + 'This action cannot be undone.';
}

export interface EventTimeSlot {
  id: number;
  url: string;
  start_time: string;
  is_available_sunday: boolean;
  is_available_monday: boolean;
  is_available_tuesday: boolean;
  is_available_wednesday: boolean;
  is_available_thursday: boolean;
  is_available_friday: boolean;
  is_available_saturday: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventScheduleException {
  id: number;
  url: string;
  roster_id: number | null;
  roster_name: string | null;
  location_id: number;
  location_name: string;
  location_url: string;
  location_area_id?: number;
  location_area_name?: string;
  location_area_url?: string;
  name: string;
  // Null on a schedule exception with no time slots: it creates no events, so
  // it has no event to shape (PRO-1433).
  duration_minutes: number | null;
  capacity: number | null;
  rolling_days: number | null;
  booking_open_time: string;
  pricing_matrix_id?: number;
  benefit_membership_id: number | null;
  benefit_membership_name: string | null;
  benefit_membership_additional_days: number;
  pricing_matrix_url?: string;
  pricing_matrix_name?: string;
  tax_rate_id: number | null;
  tax_rate_name: string | null;
  time_slots: string[];
  dates: string[];
  created_at: string;
  updated_at: string;
}

export interface EventPartInstructor {
  id: number;
  first_name: string;
  last_name: string;
  is_sub: boolean;
}

export interface NonRepeatingEventPart {
  // Editable fields
  start_time: string;
  start_date: string;
  duration_minutes: number;
  instructors: EventPartInstructor[];
  // Read-only fields
  id?: number;
  start_datetime_isoformat: string;
  end_datetime_isoformat: string;
}

/**
 * The equivalent of EventSchedule for non-repeating event types, where events
 * are configured manually rather than indirectly via schedules. Non-repeating
 * events may have multiple parts, unlike events created via schedules.
 */
export interface NonRepeatingEvent {
  id: number;
  event_type_id: number;
  event_type_name: string;
  location_id: number;
  location_name: string;
  location_area_id?: number | null;
  location_area_name?: string | null;
  capacity: number;
  rolling_days: number;
  rolling_days_use_exact_date: boolean;
  booking_open_time: string;
  pricing_matrix_id?: number;
  tax_rate_id: number | null;
  event_parts: NonRepeatingEventPart[];
  is_private: boolean;
  request_form_booking_id: number | null;
  private_notes: string;
  occurrence_name: string;
  customer_facing_name: string;
  benefit_membership_id: number | null;
  benefit_membership_name: string | null;
  benefit_membership_additional_days: number;
  created_at: string;
  updated_at: string;
}

/**
 * An event type with included pricing matrices. Returned from a specialized API
 * endpoint which avoids the need to make separate queries for each event type
 * when all pricing matrices in an organization are required.
 */
export interface EventTypeWithPricingMatrices {
  id: number;
  status: EventTypeStatus;
  name: string;
  pricing_matrices: EventPricingMatrix[];
}

export interface EventDiscountPricingMatrixDetail {
  id: number;
  name: string;
  event_type_id: number;
  event_type_name: string;
  event_type_status: EventTypeStatus;
}

export interface EventDiscount {
  id: number;
  url: string;
  is_automatic: boolean;
  code: string;
  name: string;
  discount_type: EventDiscountType;
  amount: number;
  is_per_participant: boolean;
  applies_to_add_ons: boolean;
  start_date?: string;
  end_date?: string;
  pricing_matrix_ids: number[];
  pricing_matrix_details: EventDiscountPricingMatrixDetail[];
  requires_association: boolean;
  requires_all_associations: boolean;
  association_ids: number[];
  association_names: string[];
  requires_membership: boolean;
  required_membership_ids: number[];
  required_membership_names: string[];
  max_number_of_uses_per_customer: number | null;
  required_no_check_in_period_days: number | null;
  min_check_in_count: number | null;
  max_check_in_count: number | null;
  min_number_of_days_without_membership: number | null;
  min_age: number | null;
  max_age: number | null;
  min_days_until_event_start: number | null;
  max_days_until_event_start: number | null;
  created_at: string;
  updated_at: string;
}

export interface EventTypeCategoryAssignedEventType {
  event_type_id: number;
  event_type_name: string;
  created_at: string;
  updated_at: string;
}

export interface EventTypeCategory {
  id: number;
  url: string;
  name: string;
  url_hash: string;
  order_number?: number;
  is_removed?: boolean;
  assigned_event_types: EventTypeCategoryAssignedEventType[];
  assigned_event_type_ids?: number[];
  created_at: string;
  updated_at: string;
}

export interface RosterScheduleSummary {
  id: number;
  name: string;
  start_date: string;
  end_date: string | null;
}

export interface Roster {
  id: number;
  url: string;
  event_type_id: number;
  event_type_name: string;
  location_id: number;
  location_name: string;
  // IANA zone of the roster's location.
  time_zone: string;
  name: string;
  capacity: number;
  order_number: number;
  schedules: RosterScheduleSummary[];
  current_member_count: number;
}

export interface EventTypeFilters {
  search?: string;
  status?: EventTypeStatus;
  schedule_type?: ScheduleType;
  waitlist_scope?: EventTypeWaitlistScope;
  event_category_id?: number;
  location_id?: number;
}

export interface EventDiscountFilters {
  search?: string;
  association_id?: number;
}

export interface RosterFilters {
  event_type_id?: number;
  event_type_status_in?: string;
}

export function loadEventTypeFiltersFromGetParams(activatedRoute: ActivatedRoute): EventTypeFilters {
  const filters: EventTypeFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);
  loadStringFilterFromGetParams(filters, 'status', initialQPs);
  loadStringFilterFromGetParams(filters, 'schedule_type', initialQPs);
  loadStringFilterFromGetParams(filters, 'waitlist_scope', initialQPs);
  loadIntFilterFromGetParams(filters, 'event_category_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);

  return filters;
}

export function loadEventDiscountFiltersFromGetParams(activatedRoute: ActivatedRoute): EventDiscountFilters {
  const filters: EventDiscountFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);

  return filters;
}

export function getScheduleTypeDisplay(scheduleType: ScheduleType): string {
  switch (scheduleType) {
    case ScheduleType.repeating:
      return 'Repeating';
    case ScheduleType.oneOff:
      return 'Multi-part/One-off';
    case ScheduleType.rolling:
      return 'Rolling';
    default:
      return 'Unknown';
  }
}

export function getEventTypeStatusDisplay(status: EventTypeStatus): string {
  switch (status) {
    case EventTypeStatus.draft:
      return 'Draft';
    case EventTypeStatus.active:
      return 'Active';
    case EventTypeStatus.retired:
      return 'Retired';
    default:
      return 'Unknown';
  }
}

export function eventPartIsEmpty(eventPart: NonRepeatingEventPart): boolean {
  return (
    (!propExistsAndIsNonEmpty(eventPart.start_time)) &&
    (!propExistsAndIsNonEmpty(eventPart.start_date)) &&
    (!propExists(eventPart.duration_minutes)) &&
    (!propExistsAndIsNonEmpty(eventPart.instructors))
  );
}
